import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { X, RefreshCw } from 'lucide-react'
import { RootState, AppDispatch } from '../../app/store'
import {
  switchTab,
  confirmTableSelection,
  freeSelectedTable,
  refreshTables,
  loadTables,
  selectTable
} from '../../features/tables/tablesSlice'
import DataTables from './DataTables'

interface TableSelectionModalProps {
  isOpen: boolean
  onClose: () => void
}

const TableSelectionModal: React.FC<TableSelectionModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    tables,
    activeTab,
    isLoadingTables,
    selectedTable
  } = useSelector((state: RootState) => state.tables)

  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')

  // Load tables when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(loadTables() as any)
    }
  }, [isOpen, dispatch])

  const handleTabSwitch = (tabIndex: number) => {
    dispatch(switchTab(tabIndex))
  }

  const handleConfirmTableSelection = () => {
    dispatch(confirmTableSelection())
    onClose()
  }

  const handleFreeSelectedTable = async () => {
    try {
      const result = await dispatch(freeSelectedTable() as any)
      if (freeSelectedTable.fulfilled.match(result) && result.payload?.success) {
        onClose()
      }
    } catch (error) {
    }
  }

  const isSelectedTableVisible = (() => {
    if (selectedTable <= 0) return false
    const current = tables.find((table) => table.id === selectedTable)
    if (!current) return false

    if (activeTab === 1) {
      return current.status === 'occupied'
    }

    if (activeTab === 2) {
      return Boolean(current.isVIP)
    }

    return true
  })()

  const isPrimaryButtonDisabled = selectedTable <= 0 || !isSelectedTableVisible || isSubmitting

  const handlePrimaryAction = async () => {
    if (isPrimaryButtonDisabled) {
      return
    }

    setIsSubmitting(true)

    try {
      if (activeTab === 1) {
        await handleFreeSelectedTable()
      } else {
        // Call API to select table
        await dispatch(selectTable(selectedTable) as any).unwrap()
        handleConfirmTableSelection()
      }
    } catch (error) {

    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRefreshTables = () => {
    dispatch(refreshTables() as any)
  }

  const handleClose = () => {
    onClose()
  }

  const handleBackdropClick = () => {
    handleClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleBackdropClick} className="relative z-50">
      {/* Backdrop matching design */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Centered container */}
      <div className="fixed inset-0 px-12 flex items-center justify-center">
        <Dialog.Panel
          className={`
            w-[600px] h-[578px] bg-white rounded-3xl
            overflow-hidden ${isRTL ? 'text-left' : 'text-right'}`}
        >
          {/* Header with tabs */}
          <div className="h-16 pl-4 pr-6 py-10  p-15 flex items-center justify-between gap-6">
            <div className="flex-1 flex  items-center gap-6">

              {/* Choose table tab */}
              <button
                className={`pb-2 ${activeTab === 0 ? 'border-b-2 border-flutter-button text-flutter-button' : 'text-text-secondary'}`}
                onClick={() => handleTabSwitch(0)}
              >
                <div className="text-base font-medium font-['IBM Plex Sans Arabic'] leading-6">
                  {t('table.choose_table')}
                </div>
              </button>
              {/* Free tables tab */}
              <button
                className={`pb-2 border-b-2 ${activeTab === 1 ? 'border-flutter-button text-flutter-button' : 'border-transparent text-text-secondary'}`}
                onClick={() => handleTabSwitch(1)}
              >
                <div className="text-base font-medium font-['IBM Plex Sans Arabic'] leading-6">
                  {t('table.free_tables')}
                </div>
              </button>
              {/* VIP tables tab - Hidden */}
              {/* <button
                className={`pb-2 border-b-2 ${activeTab === 2 ? 'border-flutter-button text-flutter-button' : 'border-transparent text-text-secondary'}`}
                onClick={() => handleTabSwitch(2)}
              >
                <div className="text-base font-medium font-['IBM Plex Sans Arabic'] leading-6">
                  {t('table.vip_tables')}
                </div>
              </button> */}
            </div>
            {/* Leading icon placeholder */}
            <button
              onClick={handleClose}
              className="h-[36px] w-[36px] bg-white rounded-xl border border-gray-200  flex items-center justify-center"
              aria-label={t('common.close')}
            >
              <X className="size-5 text-gray-600" />
            </button>


          </div>

          {/* Body */}
          <div className="flex-1 px-4 py-2 bg-white flex flex-col gap-6 overflow-hidden h-[calc(578px-80px-80px)]">
            <div className="flex-1 overflow-y-auto">
              {isLoadingTables ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flutter-button mx-auto mb-4"></div>
                    <p className="text-base font-medium text-gray-600 font-['IBM Plex Sans Arabic']">
                      {t('table.loading_tables')}
                    </p>
                  </div>
                </div>
              ) : (
                <DataTables />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="w-full h-[20px] p-5 pt-[40px] bg-white border-t border-border-soft inline-flex justify-between items-center gap-4">
            {/* Legend */}
            <div className="flex justify-start items-center gap-2">
              {/* Occupied/Used indicator */}
              <div className="bg-white rounded-[32px] flex justify-center items-center gap-1.5 px-3 py-1">
                <div className="text-right justify-start text-text-secondary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                  {t('table.occupied')}
                </div>
                <div className="w-2 h-2 bg-red-600 rounded-full" />
              </div>
              {/* Available/Free indicator */}
              <div className="bg-white rounded-[32px] flex justify-center items-center gap-1.5 px-3 py-1">
                <div className="text-right justify-start text-text-secondary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                  {t('table.available')}
                </div>
                <div className="w-2 h-2 bg-blue-600 rounded-full" />
              </div>
              {/* VIP indicator - Hidden */}
              {/* <div className="bg-white rounded-[32px] flex justify-center items-center gap-1.5 px-3 py-1">
                <div className="text-right justify-start text-text-secondary text-sm font-normal font-['IBM Plex Sans Arabic'] leading-5">
                  {t('table.vip_tables')}
                </div>
                <div className="w-2 h-2 bg-[#f6b53d] rounded-full" />
              </div> */}
            </div>

            {/* Primary button */}
            <div className="h-12 px-0">
              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={isPrimaryButtonDisabled}
                className={`w-[75px] h-[50px] rounded-lg font-semibold text-base leading-6 font-['IBM Plex Sans Arabic'] transition-all flex items-center justify-center ${isPrimaryButtonDisabled
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-flutter-button text-white hover:opacity-90 active:scale-[0.98]'
                  }`}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  activeTab === 1 ? t('table.free') : t('common.done')
                )}
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default TableSelectionModal
